/**
 * KioskView.tsx — l'écran que voient les invités à l'événement.
 * 4 états : attract → countdown → result → delivery.
 * Porté depuis snapbooth-tempalte/project/kiosk.jsx.
 *
 * Mode démo : cycle auto entre les états via la admin bar. En prod, la
 * caméra + l'imprimante seraient câblées sur le hardware réel.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye,
  X,
  Sparkles,
  Star,
  Heart,
  Camera,
  Check,
  Printer,
  QrCode,
  Send,
  RotateCcw,
} from 'lucide-react';
import { studioStatsService, type StudioStats } from '../services/StudioStatsService';

type KioskState = 'attract' | 'countdown' | 'result' | 'delivery';
const TOTAL_SHOTS = 4;

export const KioskView: React.FC = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<KioskState>('attract');
  const [shotIndex, setShotIndex] = useState(0);
  const [count, setCount] = useState(3);
  const [stats, setStats] = useState<StudioStats | null>(null);

  // Identité d'événement + stats RÉELLES (projet le plus récent, exports).
  useEffect(() => {
    studioStatsService.getStudioStats().then(setStats);
  }, []);

  // A21 : s'abonner aux nouveaux exports en temps réel (comme LiveView).
  // À chaque export détecté, on recharge les stats globales pour que le
  // compteur "souvenirs exportés" se mette à jour sans rechargement manuel.
  useEffect(() => {
    const unsubscribe = studioStatsService.subscribeToExports(() => {
      studioStatsService.getStudioStats().then(setStats);
    });
    return unsubscribe;
  }, []);

  // A21 : rafraîchissement manuel des stats (admin bar).
  const refreshStats = () => {
    studioStatsService.getStudioStats().then(setStats);
  };

  const eventName = stats?.latestProjectName ?? 'Votre événement';

  // Auto-cycle countdown pour la démo
  useEffect(() => {
    if (state !== 'countdown') return;
    const t = setTimeout(() => {
      if (count > 1) {
        setCount(c => c - 1);
      } else if (shotIndex < TOTAL_SHOTS - 1) {
        setShotIndex(i => i + 1);
        setCount(3);
      } else {
        setState('result');
      }
    }, 900);
    return () => clearTimeout(t);
  }, [state, count, shotIndex]);

  const start = () => {
    setShotIndex(0);
    setCount(3);
    setState('countdown');
  };

  const restart = () => {
    setShotIndex(0);
    setCount(3);
    setState('attract');
  };

  const goTo = (s: KioskState) => {
    setShotIndex(0);
    setCount(3);
    setState(s);
  };

  return (
    <div className="kiosk-view" data-screen-label="Kiosk">
      {/* Admin chrome — n'apparaît PAS en mode plein écran prod */}
      <div className="kiosk-admin-bar">
        <div className="kiosk-admin-left">
          <Eye size={12} />
          {/* M8 : clarifier explicitement que c'est un aperçu UI (pas de caméra/
              imprimante réelle connectée) pour éviter toute ambiguïté en démonstration. */}
          <span>Aperçu UI — simulation visuelle (aucune imprimante ni caméra connectée)</span>
        </div>
        <div className="kiosk-admin-tabs">
          {(
            [
              ['attract', 'Accueil'],
              ['countdown', 'Capture'],
              ['result', 'Résultat'],
              ['delivery', 'Livraison'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              className={`kiosk-admin-tab ${state === id ? 'is-active' : ''}`}
              onClick={() => goTo(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {/* A21 : rafraîchir manuellement les stats (utile si la subscription
              Supabase est temporairement indisponible). */}
          <button
            className="kiosk-admin-exit"
            onClick={refreshStats}
            title="Rafraîchir les statistiques"
          >
            <RotateCcw size={13} />
            Rafraîchir
          </button>
          <button className="kiosk-admin-exit" onClick={() => navigate('/dashboard')}>
            <X size={13} />
            Quitter le kiosque
          </button>
        </div>
      </div>

      {/* Écran kiosque (frame avec ombres / fond violet) */}
      <div className="kiosk-frame">
        <div className="kiosk-screen">
          {state === 'attract' && (
            <KioskAttract
              onStart={start}
              eventName={eventName}
              exportsCount={stats?.exportsCount ?? 0}
              projectsCount={stats?.projectsCount ?? 0}
            />
          )}
          {state === 'countdown' && <KioskCountdown count={count} shotIndex={shotIndex} />}
          {state === 'result' && (
            <KioskResult
              onAgain={restart}
              onContinue={() => setState('delivery')}
              eventName={eventName}
            />
          )}
          {state === 'delivery' && <KioskDelivery onDone={restart} />}
        </div>
      </div>
    </div>
  );
};

/* ─── ATTRACT ─── */
const KioskAttract: React.FC<{
  onStart: () => void;
  eventName: string;
  exportsCount: number;
  projectsCount: number;
}> = ({ onStart, eventName, exportsCount, projectsCount }) => {
  const sparkleIcons = [Star, Sparkles, Heart];
  return (
    <div className="kiosk-attract">
      <div className="kiosk-sparkles" aria-hidden="true">
        {Array.from({ length: 8 }).map((_, i) => {
          const Ico = sparkleIcons[i % 3];
          return (
            <div key={i} className={`k-sparkle k-s-${i}`}>
              <Ico size={14 + (i % 3) * 4} />
            </div>
          );
        })}
      </div>

      <div className="kiosk-attract-content">
        <div className="kiosk-event-chip">
          <Sparkles size={14} />
          <span>{eventName}</span>
        </div>

        <h1 className="kiosk-h1">
          <span>Souvenirs</span>
          <span className="italic">à emporter</span>
        </h1>

        <p className="kiosk-lede">
          Posez devant l'objectif, on s'occupe du reste.
          <br />4 photos, 8 secondes — votre tirage est imprimé tout de suite.
        </p>

        <button className="kiosk-cta" onClick={onStart}>
          <span className="kiosk-cta-glow" />
          <span
            style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 12 }}
          >
            <Camera size={22} />
            Tap pour commencer
          </span>
        </button>

        <div className="kiosk-attract-stats">
          <div>
            <div className="kiosk-stat-n">{exportsCount.toLocaleString('fr-FR')}</div>
            <div className="kiosk-stat-l">souvenirs exportés</div>
          </div>
          <div className="kiosk-stat-divider" />
          <div>
            <div className="kiosk-stat-n">{projectsCount.toLocaleString('fr-FR')}</div>
            <div className="kiosk-stat-l">designs créés</div>
          </div>
          <div className="kiosk-stat-divider" />
          <div>
            <div className="kiosk-stat-n">😄</div>
            <div className="kiosk-stat-l">sourires garantis</div>
          </div>
        </div>
      </div>

      <div className="kiosk-attract-hint">Touchez l'écran pour démarrer</div>
    </div>
  );
};

/* ─── COUNTDOWN ─── */
const KioskCountdown: React.FC<{ count: number; shotIndex: number }> = ({ count, shotIndex }) => {
  return (
    <div className="kiosk-capture">
      <div className="kiosk-feed">
        <div className="kiosk-feed-img">
          <div className="k-feed-bg" />
          <div className="k-feed-people">
            <div className="k-person p1" />
            <div className="k-person p2" />
          </div>
          <div className="k-feed-grid" />

          <div className="kiosk-count-overlay">
            <div className="kiosk-count-ring" />
            <div key={count} className="kiosk-count-number">
              {count}
            </div>
          </div>

          <div className="kiosk-capture-hint">Souriez !</div>
        </div>
      </div>

      <div className="kiosk-shot-dots">
        {Array.from({ length: TOTAL_SHOTS }).map((_, i) => (
          <div
            key={i}
            className={`kiosk-shot-dot ${i < shotIndex ? 'done' : ''} ${i === shotIndex ? 'current' : ''}`}
          >
            {i < shotIndex ? <Check size={12} /> : i + 1}
          </div>
        ))}
      </div>
      <div className="kiosk-shot-label">
        Photo <strong>{shotIndex + 1}</strong> sur {TOTAL_SHOTS}
      </div>
    </div>
  );
};

/* ─── RESULT ─── */
const KioskResult: React.FC<{
  onAgain: () => void;
  onContinue: () => void;
  eventName: string;
}> = ({ onAgain, onContinue, eventName }) => {
  return (
    <div className="kiosk-result">
      <div className="kiosk-result-header">
        <h2 className="kiosk-h2">
          Voilà votre souvenir <span className="italic">!</span>
        </h2>
        <p className="kiosk-result-sub">Choisissez comment le recevoir</p>
      </div>

      <div className="kiosk-result-stage">
        <div className="kiosk-print-preview">
          <div className="kiosk-print-sheet">{eventName}</div>
          <div className="kiosk-print-shadow" />
        </div>

        <div className="kiosk-strip">
          <div className="kiosk-strip-title">Vos 4 photos</div>
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="kiosk-strip-photo">
              <div className="k-feed-bg" style={{ filter: `hue-rotate(${i * 30}deg)` }} />
              <div className="k-feed-people" style={{ transform: 'scale(0.4)' }}>
                <div className="k-person p1" />
                <div className="k-person p2" />
              </div>
              <div className="kiosk-strip-num">{i}</div>
            </div>
          ))}
        </div>
      </div>

      {/* M8 : noter que ces actions sont des simulations visuelles (UI preview).
          En production, elles seraient câblées à l'imprimante, au générateur de
          QR et au service d'envoi d'e-mail. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 12,
          padding: '4px 10px',
          borderRadius: 999,
          background: 'rgba(255,255,255,0.08)',
          fontSize: 11,
          fontWeight: 600,
          color: 'rgba(255,255,255,0.5)',
          alignSelf: 'center',
        }}
      >
        <Eye size={11} />
        Aperçu UI — actions non câblées en mode démo
      </div>
      <div className="kiosk-result-actions">
        <button className="kiosk-action secondary" onClick={onAgain}>
          <RotateCcw size={20} />
          Refaire
        </button>
        <button
          className="kiosk-action"
          onClick={onContinue}
          title="Simulation — aucune imprimante connectée"
        >
          <Printer size={20} />
          <span>
            Imprimer<small>Démo</small>
          </span>
        </button>
        <button className="kiosk-action" onClick={onContinue} title="Simulation — aucun QR généré">
          <QrCode size={20} />
          <span>
            Mon téléphone<small>Démo</small>
          </span>
        </button>
        <button
          className="kiosk-action"
          onClick={onContinue}
          title="Simulation — aucun e-mail envoyé"
        >
          <Send size={20} />
          <span>
            Par e-mail<small>Démo</small>
          </span>
        </button>
      </div>
    </div>
  );
};

/* ─── DELIVERY ─── */
const KioskDelivery: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  return (
    <div className="kiosk-delivery">
      <div className="kiosk-delivery-icon">
        <div className="kiosk-delivery-ring" />
        <div className="kiosk-delivery-ring-2" />
        <Printer size={56} />
      </div>
      <h2 className="kiosk-h2">Impression en cours…</h2>
      <p className="kiosk-result-sub" style={{ marginBottom: 16 }}>
        Récupérez votre tirage dans 12 secondes au bac de sortie
      </p>

      {/* M8 : badge démo bien visible pour éviter toute confusion (aucune imprimante
          réelle n'est pilotée — la barre de progression est purement visuelle). */}
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '5px 14px',
          borderRadius: 999,
          border: '1px solid rgba(255,255,255,0.2)',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.5)',
          marginBottom: 20,
        }}
      >
        <Eye size={11} />
        Simulation — aucune imprimante connectée
      </div>

      <div className="kiosk-progress">
        <div className="kiosk-progress-bar">
          <div />
        </div>
        <div className="kiosk-progress-label">
          <span>Photo › impression (démo)</span>
          <span>78 %</span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, marginTop: 40 }}>
        <button className="kiosk-action secondary" onClick={onDone}>
          <Check size={20} />
          Terminé
        </button>
        <button className="kiosk-action" onClick={onDone}>
          <Send size={20} />
          Envoyer aussi par e-mail
        </button>
      </div>

      <div className="kiosk-delivery-tip">💡 Pendant ce temps, faites passer votre suivant !</div>
    </div>
  );
};
